/** @prettier */
//-----------------------------
//	グラフデータ作成処理
//-----------------------------
import {graph} from '../define';
import {translateDate} from './graphFunction';
import {TFunction} from 'i18next';

/**
 * グラフデータ作成処理
 * @param {string} dateRange グラフの日付範囲(Day|Week)
 * @param {string} graphData グラフに表示する状態データ
 * @param {string} graphTargetID グラフ対象のID
 * @param {string} graphType グラフの種類(Line, Bar, None)
 * @param {string} timeZone タイムゾーン
 * @param {string} stateName 状態値の名前(ex: sl-states,modbus-states)
 * @param {string} states 状態値(ex: states, indoor-states)
 * @param {string} idName データ範囲のID
 * @param {string} graphStartDate グラフデータ取得開始日時
 * @param {string} graphEndDate グラフデータ取得終了日時
 * @param {TFunction} trans 翻訳関数
 * @returns グラフ描画用データ
 */
export const makeGraphData = (
  dateRange: string,
  graphData: any,
  graphTargetID: string,
  graphType: string,
  timeZone: string,
  stateName: string,
  states: string,
  idName: string,
  graphStartDate: string,
  graphEndDate: string,
  trans: TFunction
) => {
  let xArray = [];
  let powerConsumptionCoolYArray = [];
  let powerConsumptionHotYArray = [];
  let powerConsumptionOtherYArray = [];

  let indoorTemperatureYArray = [];
  let outdoorTemperatureYArray = [];
  let presetTemperatureYArray = [];

  let dateFormat = 'HH'; /* グラフの軸表示フォーマット 初期値：1日 */
  let pickupDate: any = []; /* グラフ表示に必要なデータのみに絞った配列 */
  let target = '0'; /* フロア番号 */
  /** 温度グラフのデータ取得間隔（msec） */
  let everyMsec = graph.TREND_REPORTING_EVERY_MSEC_DAY;

  /* データ無し時の初期値データを作成 */
  let iniData = [];
  for (let i = 0; i < 24; i++) iniData.push({hour: i});
  const retIni = {
    date: [],
    tempInY: [],
    tempOutY: [],
    tempPreY: [],
    powerCool: [],
    powerHot: [],
    powerOther: [],
  };
  if (graphType === 'line') {
    /* 1日or1週間の切替 */
    if (dateRange === 'day') {
      dateFormat = 'HH:mm';
    } else {
      dateFormat = 'MMM.dd';
      everyMsec = graph.TREND_REPORTING_EVERY_MSEC_WEEK;
    }
  } else {
    /* 期間の切替 */
    /* グラフx軸ラベル */
    switch (dateRange) {
      case 'day':
      default:
        dateFormat = 'HH:mm';
        break;
      case 'week':
        dateFormat = 'MMM.dd';
        break;
      case 'month':
        dateFormat = 'dd';
        break;
      case 'year':
        dateFormat = 'MMM.';
        break;
      case 'tenYear':
        dateFormat = 'yyyy';
        break;
    }
  }

  if (graphData != null && graphData[stateName].length > 0) {
    /* どのフロアのグラフボタンが押されたか判定 */
    for (let i in graphData[stateName][0][states]) {
      if (
        graphTargetID === graphData[stateName][0][states][Number(i)][idName]
      ) {
        target = i;
      }
    }

    /* APIから取得したデータをグラフ用配列に格納 */
    //graphData[stateName]のindex番号は終了日のデータが始まっているため逆順で格納する
    for (let data of graphData[stateName].reverse()) {
      pickupDate.push(data);
    }
    /* 温度グラフデータの作成 */
    if (graphType === 'line') {
      /** データ存在確認用の日時（unixTime） */
      let confirmationDate = new Date(graphStartDate).getTime();
      /** 時間情報とnullデータを追加し、確認用の日時をデータ取得間隔分進める処理 */
      const addMissingData = () => {
        //時間情報の追加
        xArray.push(
          translateDate(new Date(confirmationDate), timeZone, dateFormat, trans)
        );
        //nullデータ追加
        indoorTemperatureYArray.push(null);
        outdoorTemperatureYArray.push(null);
        presetTemperatureYArray.push(null);
        //データ存在確認用日時を次のデータ取得時間へと進める
        confirmationDate = confirmationDate + everyMsec;
      };
      /* グラフ関数へ渡すデータを作成する */
      for (let data of pickupDate) {
        //データが欠けている時間帯がある場合、データを追加する
        while (confirmationDate !== new Date(data['date']).getTime()) {
          addMissingData();
        }
        /* 時間情報の追加 */
        xArray.push(translateDate(data['date'], timeZone, dateFormat, trans));
        /* データ部が存在するか確認 */
        if (data[states].length > 0) {
          /* データが空欄でないか確認(空欄をグラフ化すると表示がバグる) */
          if (stateName === 'modbus-states') {
            //prettier-ignore
            if (data[states][Number(target)]['inside-temperature'] !== '') {
               indoorTemperatureYArray.push(
                 Number(data[states][Number(target)]['inside-temperature'])
               );
              // indoorTemperatureYArray.push(Math.round(Math.random() * ( 35 - 1 ) + 1))	//Debug用コード
            } else {
              indoorTemperatureYArray.push(null);
            }
          } else {
            if (data[states][Number(target)]['indoor-temperature'] !== '') {
              indoorTemperatureYArray.push(
                Number(data[states][Number(target)]['indoor-temperature'])
              );
              /*
              indoorTemperatureYArray.push(
                Math.round(Math.random() * (35 - 1) + 1)
              ); //Debug用コード
              */
            } else {
              indoorTemperatureYArray.push(null);
            }
          }
          if (data[states][Number(target)]['outdoor-temperature'] !== '') {
            outdoorTemperatureYArray.push(
              Number(data[states][Number(target)]['outdoor-temperature'])
            );
            /*
            outdoorTemperatureYArray.push(
              Math.round(Math.random() * (35 - 1) + 1)
            ); //Debug用コード
            */
          } else {
            outdoorTemperatureYArray.push(null);
          }
          if (data[states][Number(target)]['preset-temperature'] !== '') {
            presetTemperatureYArray.push(
              Number(data[states][Number(target)]['preset-temperature'])
            );
            /*
            presetTemperatureYArray.push(
              Math.round(Math.random() * (35 - 1) + 1)
            ); //Debug用コード
            */
          } else {
            presetTemperatureYArray.push(null);
          }
        } else {
          indoorTemperatureYArray.push(null);
          outdoorTemperatureYArray.push(null);
          presetTemperatureYArray.push(null);
        }
        //データ存在確認用日時を次のデータ取得時間へと進める
        confirmationDate = confirmationDate + everyMsec;
      }
      //データ取得終了日時までにデータが取得できていない時間帯がある場合、データを追加する
      while (confirmationDate !== new Date(graphEndDate).getTime()) {
        addMissingData();
      }
      /**
       * 戻り値の設定
       */
      const ret = {
        powerCool: [],
        powerHot: [],
        powerOther: [],
        date: xArray,
        tempInY: indoorTemperatureYArray,
        tempOutY: outdoorTemperatureYArray,
        tempPreY: presetTemperatureYArray,
      };
      return ret;
    } else {
      /* 消費電力グラフデータの作成 */
      /* グラフ関数へ渡すデータを作成する */
      for (let data of pickupDate) {
        /* 時間情報の追加 */
        xArray.push(translateDate(data['date'], timeZone, dateFormat, trans));
        /* データ部が存在するか確認 */
        if (data[states].length > 0) {
          /* データが空欄でないか確認(空欄をグラフ化すると表示がバグる) */
          if (
            data[states][Number(target)]['power-consumption'][
              'cooling-mode'
            ] !== '' ||
            data[states][Number(target)]['power-consumption'][
              'heating-mode'
            ] !== '' ||
            data[states][Number(target)]['power-consumption']['other-mode'] !==
              ''
          ) {
            powerConsumptionCoolYArray.push(
              Math.round(
                Number(
                  data[states][Number(target)]['power-consumption'][
                    'cooling-mode'
                  ]
                ) / 1000
              )
            );
            powerConsumptionHotYArray.push(
              Math.round(
                Number(
                  data[states][Number(target)]['power-consumption'][
                    'heating-mode'
                  ]
                ) / 1000
              )
            );
            powerConsumptionOtherYArray.push(
              Math.round(
                Number(
                  data[states][Number(target)]['power-consumption'][
                    'other-mode'
                  ]
                ) / 1000
              )
            );
            /*
            powerConsumptionCoolYArray.push(
              Number(Math.round(Math.random() * (35 - 1) + 1))
            ); //Debug用コード
            powerConsumptionHotYArray.push(
              Number(Math.round(Math.random() * (35 - 1) + 1))
            ); //Debug用コード
            powerConsumptionOtherYArray.push(
              Number(Math.round(Math.random() * (35 - 1) + 1))
            ); //Debug用コード	}
            */
          } else {
            powerConsumptionCoolYArray.push(null);
            powerConsumptionHotYArray.push(null);
            powerConsumptionOtherYArray.push(null);
          }
        } else {
          powerConsumptionCoolYArray.push(null);
          powerConsumptionHotYArray.push(null);
          powerConsumptionOtherYArray.push(null);
        }
      }
      /**
       * 戻り値の設定
       */
      const ret = {
        powerCool: powerConsumptionCoolYArray,
        powerHot: powerConsumptionHotYArray,
        powerOther: powerConsumptionOtherYArray,
        date: xArray,
        tempInY: [],
        tempOutY: [],
        tempPreY: [],
      };
      return ret;
    }
  }
  return retIni;
};
